import React, { useState } from 'react'
import { Layers, Check } from 'lucide-react'

export interface MapLayer {
  id: string
  name: string
  style: string
  description?: string
}

interface MapLayerControlProps {
  onLayerChange: (layerId: string) => void
  currentLayer: string
  className?: string
}

export const MAP_LAYERS: MapLayer[] = [
  {
    id: 'satellite',
    name: 'Mapbox Satellite',
    style: 'mapbox://styles/mapbox/satellite-streets-v12',
    description: 'Satellite imagery with roads and labels'
  },
  {
    id: 'google-satellite',
    name: 'Google Satellite',
    style: 'google-satellite',
    description: 'Google satellite imagery'
  },
  {
    id: 'google-hybrid',
    name: 'Google Hybrid',
    style: 'google-hybrid',
    description: 'Google satellite with roads and labels'
  },
  {
    id: 'google-terrain',
    name: 'Google Terrain',
    style: 'google-terrain',
    description: 'Google terrain with elevation contours'
  },
  {
    id: 'outdoors',
    name: 'Mapbox Outdoors',
    style: 'mapbox://styles/mapbox/outdoors-v12',
    description: 'Topographic maps for outdoor activities'
  },
  {
    id: 'streets',
    name: 'Mapbox Streets',
    style: 'mapbox://styles/mapbox/streets-v12',
    description: 'Street map with detailed roads'
  },
  {
    id: 'basic',
    name: 'Mapbox Basic',
    style: 'mapbox://styles/mapbox/basic-v9',
    description: 'Simple, clean base map'
  },
  {
    id: 'standard',
    name: 'Mapbox Standard',
    style: 'mapbox://styles/mapbox/standard',
    description: 'Modern 3D map with dynamic lighting'
  },
  {
    id: 'navigation-day',
    name: 'Navigation Guidance Day',
    style: 'mapbox://styles/mapbox/navigation-guidance-day-v4',
    description: 'Optimized for daytime navigation'
  },
  {
    id: 'navigation-night',
    name: 'Navigation Guidance Night',
    style: 'mapbox://styles/mapbox/navigation-guidance-night-v4',
    description: 'Optimized for nighttime navigation'
  },
  {
    id: 'light',
    name: 'Mapbox Light',
    style: 'mapbox://styles/mapbox/light-v11',
    description: 'Clean light themed map'
  },
  {
    id: 'dark',
    name: 'Mapbox Dark',
    style: 'mapbox://styles/mapbox/dark-v11',
    description: 'Dark themed map'
  }
]

export function MapLayerControl({ onLayerChange, currentLayer, className = '' }: MapLayerControlProps) {
  const [isOpen, setIsOpen] = useState(false)

  const currentLayerData = MAP_LAYERS.find(layer => layer.id === currentLayer) || MAP_LAYERS[0]

  const handleLayerSelect = (layerId: string) => {
    onLayerChange(layerId)
    setIsOpen(false)
  }

  const isEmbed = className.includes('embed-control')
  
  return (
    <div className={isEmbed ? 'relative' : `relative ${className}`}>
      {/* Control Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={isEmbed ? className : "text-gray-200 hover:bg-gray-700/50 transition-colors flex items-center justify-center w-full h-[32px]"}
        title={`Map style: ${currentLayerData.name}`}
        style={isEmbed ? { display: 'flex', alignItems: 'center', justifyContent: 'center' } : {}}
      >
        <Layers className={isEmbed ? 'w-[18px] h-[18px]' : 'w-4 h-4'} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <div 
            className="absolute bg-gray-800/95 backdrop-blur-sm rounded-lg shadow-xl" 
            style={{ 
              width: '256px',
              right: '0',
              top: '30px',
              zIndex: 9999
            }}>
            <div className="p-2">
              <div className="text-[10px] text-gray-300 font-medium mb-2 px-2">
                Map Layers
              </div>
              
              {MAP_LAYERS.map((layer) => (
                <button
                  key={layer.id}
                  onClick={() => handleLayerSelect(layer.id)}
                  className="relative w-full text-left px-3 py-2 rounded-md hover:bg-gray-700 transition-colors"
                  style={{ minWidth: '240px' }}
                >
                  <div className="pr-6">
                    <div className="text-xs font-medium text-white">
                      {layer.name}
                    </div>
                    {layer.description && (
                      <div className="text-[10px] text-gray-400 mt-0.5">
                        {layer.description}
                      </div>
                    )}
                  </div>
                  
                  {currentLayer === layer.id && (
                    <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}